import React, { useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ConfirmProvider } from "./components/ui/confirm-dialog";
import { WebSocketProvider } from "./context/WebSocketContext";
import { Toaster } from "sonner";
// Eager: entrada y shell (críticos para el primer render / pre-router)
import Login from "./pages/Login";
import DashboardLayout from "./pages/DashboardLayout";
import MaintenancePage from "./pages/MaintenancePage";
// Lazy: resto de páginas → code-splitting (cada una entra a su propio chunk y
// solo se descarga al navegar a su ruta). Reduce el JS de arranque.
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const DashboardHome = lazy(() => import("./pages/DashboardHome"));
const MyPetitions = lazy(() => import("./pages/MyPetitions"));
const CreatePetition = lazy(() => import("./pages/CreatePetition"));
const PetitionDetail = lazy(() => import("./pages/PetitionDetail"));
const AllPetitions = lazy(() => import("./pages/AllPetitions"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const EstadisticasUnificadas = lazy(() => import("./pages/EstadisticasUnificadas"));
const Predios = lazy(() => import("./pages/Predios"));
const VisorPredios = lazy(() => import("./pages/VisorPredios"));
const Pendientes = lazy(() => import("./pages/Pendientes"));

// Inicio según rol. La Subdirección solo firma la M4 que le asignan: su Inicio
// es su propia página (qué espera su firma, qué viene, qué firmó).
function InicioPorRol() {
  const { user } = useAuth();
  return user?.role === 'subdireccion' ? <InicioSubdireccion /> : <DashboardHome />;
}
const FileManager = lazy(() => import("./pages/FileManager"));
const ProyectosActualizacion = lazy(() => import("./pages/ProyectosActualizacion"));
const InicioSubdireccion = lazy(() => import("./pages/InicioSubdireccion"));
const ProyectoActualizacion = lazy(() => import('./pages/ProyectoActualizacion'));
const OperacionActualizacion = lazy(() => import('./pages/OperacionActualizacion'));
const VisorActualizacion = lazy(() => import("./pages/VisorActualizacion"));
const GestionPropuestas = lazy(() => import("./pages/GestionPropuestas"));
const CertificadosGestion = lazy(() => import("./pages/CertificadosGestion"));
const GestionPrediosActualizacion = lazy(() => import("./pages/GestionPrediosActualizacion"));
const GuardaEnCalidad = lazy(() => import("./components/actualizacion/GuardaEnCalidad"));
const BalanceCalidadActualizacion = lazy(() => import("./pages/BalanceCalidadActualizacion"));
const ConfiguracionResoluciones = lazy(() => import("./pages/ConfiguracionResoluciones"));
const MutacionesResoluciones = lazy(() => import("./pages/MutacionesResoluciones"));
const LogActividades = lazy(() => import("./pages/LogActividades"));
import { OfflineIndicator, OnlineIndicator, PWAInstallPrompt } from "./components/OfflineComponents";
import { startVersionCheck } from "./utils/versionCheck";

// Fallback mientras se descarga el chunk de una página lazy
function RouteFallback() {
  return (
    <div className="flex items-center justify-center w-full h-full min-h-[60vh]" role="status" aria-live="polite">
      <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-300 border-t-slate-600" aria-hidden="true"></div>
      <span className="sr-only">Cargando…</span>
    </div>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function App() {
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceLoading, setMaintenanceLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Iniciar verificación de versión: detecta deploys nuevos y limpia caché del
    // navegador automáticamente (IndexedDB + Cache API + SW), sin perder sesión.
    // Se DIFIERE a tiempo ocioso del navegador: no es crítica para el primer
    // render, así no compite con la carga inicial (regla js-request-idle-callback).
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => startVersionCheck(), { timeout: 3000 });
    } else {
      setTimeout(() => startVersionCheck(), 2000);
    }

    // Check maintenance status
    fetch(`${BACKEND_URL}/api/maintenance/status`)
      .then(res => res.json())
      .then(data => {
        setMaintenanceMode(data.enabled);
      })
      .catch(() => {
        setMaintenanceMode(false);
      })
      .finally(() => {
        setMaintenanceLoading(false);
      });

    // Check if current user is admin from stored token
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.role === 'administrador' || payload.role === 'coordinador') {
          setIsAdmin(true);
        }
      }
    } catch {
      // Invalid token or not logged in
    }
  }, []);

  // Show maintenance page for non-admin users when maintenance is active
  if (!maintenanceLoading && maintenanceMode && !isAdmin) {
    return <MaintenancePage />;
  }

  return (
    <AuthProvider>
      <Toaster
        position="top-center"
        richColors
        expand={true}
        visibleToasts={5}
        closeButton
        duration={6000}
        toastOptions={{
          style: { zIndex: 2147483647 },
          className: 'toast-above-all'
        }}
        containerStyle={{ zIndex: 2147483647 }}
      />
      <WebSocketProvider>
        {/* Confirmaciones dentro de la página (reemplazan window.confirm, que el
            navegador puede silenciar con "No permitir más diálogos"). */}
        <ConfirmProvider>
        <BrowserRouter>
          {maintenanceMode && isAdmin && (
            <div className="bg-amber-500 text-white text-center text-sm py-1 px-4 font-medium">
              Modo mantenimiento activo — Solo visible para administradores
            </div>
          )}
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/dashboard" element={<DashboardLayout />}>
            <Route index element={<InicioPorRol />} />
            <Route path="peticiones" element={<MyPetitions />} />
            <Route path="peticiones/nueva" element={<CreatePetition />} />
            <Route path="peticiones/:id" element={<PetitionDetail />} />
            <Route path="todas-peticiones" element={<AllPetitions />} />
            <Route path="usuarios" element={<UserManagement />} />
            <Route path="estadisticas" element={<EstadisticasUnificadas />} />
            <Route path="predios" element={<Predios />} />
            <Route path="visor-predios" element={<VisorPredios />} />
            <Route path="pendientes" element={<Pendientes />} />
            <Route path="gestor-recursos" element={<FileManager />} />
            <Route path="proyectos-actualizacion" element={<ProyectosActualizacion />} />
            <Route path="proyecto-actualizacion/:proyectoId" element={<GuardaEnCalidad><ProyectoActualizacion /></GuardaEnCalidad>} />
            {/* El balance de calidad abre aunque el proyecto esté en calidad: desde ahí se corrige */}
            <Route path="proyecto-actualizacion/:proyectoId/calidad" element={<BalanceCalidadActualizacion />} />
            <Route path="proyecto-actualizacion/:proyectoId/operacion" element={<GuardaEnCalidad><OperacionActualizacion /></GuardaEnCalidad>} />
            <Route path="gestion-predios-actualizacion/:proyectoId" element={<GuardaEnCalidad><GestionPrediosActualizacion /></GuardaEnCalidad>} />
            <Route path="visor-actualizacion/:proyectoId" element={<GuardaEnCalidad><VisorActualizacion /></GuardaEnCalidad>} />
            <Route path="gestion-propuestas" element={<GuardaEnCalidad><GestionPropuestas /></GuardaEnCalidad>} />
            <Route path="certificados" element={<CertificadosGestion />} />
            <Route path="configuracion-resoluciones" element={<ConfiguracionResoluciones />} />
            <Route path="mutaciones-resoluciones" element={<MutacionesResoluciones />} />
            <Route path="log-actividades" element={<LogActividades />} />
          </Route>
        </Routes>
          </Suspense>
        </BrowserRouter>

        {/* PWA Components */}
        <OfflineIndicator />
        <OnlineIndicator />
        <PWAInstallPrompt />
        </ConfirmProvider>
      </WebSocketProvider>
    </AuthProvider>
  );
}

export default App;
